/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useState, useContext, useEffect } from "react";
import { Banner, ResourceList } from "lib/components";
import { QuickReport } from "app/reports";
import { useQuery, useMutation } from "@apollo/client";
import {
  Badge,
  Button,
  Text,
  LegacyCard as Card,
  ResourceItem,
  InlineStack,
  Tabs,
  Thumbnail,
  BlockStack,
} from "@shopify/polaris";

import { baseHelper, imageHelper, errorHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";
import MoreFilters, { Filter } from "app/productOld/modules/generic/moreFilters/moreFilter";

import GET_TEMP_PRODUCT_LIST from "app/productOld/apollo/queries/getTempProductList";
import RE_SYNC_PRODUCT from "app/productOld/apollo/mutations/reSync";
import { SYNC_SHOPIFY_PRODUCT } from "app/productOld/apollo/mutations";
import TEMP_PRODUCT_SUBSCRIPTION from "app/productOld/apollo/subscriptions/tempProductList";

import constant from "lib/constant/constant";
import { withErrorBoundary, withFeature } from "lib/hoc";
import Popover from "lib/components/popover/popover";
import { useLocation, useNavigate } from "react-router-dom";

const { SELECTED_FILTER, TAGGED_WITH, TITLE_ASC, TITLE_DESC, UPDATE_ASC, UPDATE_DESC } = constant;

const List = () => {
  const { cms } = useContext(PrivateContext);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = baseHelper.getQueryParams(location.search);
  const [selected, setSelected] = useState(parseInt(queryParams.tab, 10) || 0);
  let searchVal = queryParams.search && queryParams.search.split("+");
  searchVal = searchVal && searchVal.length && searchVal.join(" ");
  const [values, setValues] = useState(searchVal || "");
  const [selectedAction, setSelectedAction] = useState("");
  const [appliedFiltersValue, setAppliedFiltersValue] = useState([]);
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(null);
  const [queryValue, setQueryValue] = useState(searchVal || "");
  const [selectedItems, setSelectedItems] = useState([]);
  const getQueryParams = baseHelper.getQueryParams(location.search);
  const [sortValue, setSortValue] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(getQueryParams.page || 1);
  const [perPage, setPerPage] = useState(getQueryParams.perPage || 20);
  const [activePopover, setActivePopover] = useState({});
  const [taggedWith, setTaggedWith] = useState(queryParams.list_search || null);
  const [selectedFilter, setSelectedFilter] = useState(queryParams.list_filter || null);
  const [dataToFetch, setDataToFetch] = useState("");
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
    action: null,
  });

  if (!getQueryParams) {
    getQueryParams.search = "";
    getQueryParams.sort_name = "";
    getQueryParams.sort_order = "";
    getQueryParams.filter = "";
  }
  if (selectedFilter === constant.TITLE) {
    setSelectedFilter(constant.TITLES);
  }

  const inputData = {
    search: getQueryParams.search,
    sort_name: getQueryParams.sort_name,
    sort_order: getQueryParams.sort_order,
    filter: getQueryParams.filter,
    list_filter: getQueryParams.list_filter,
    list_search: getQueryParams.list_search,
    page: parseInt(getQueryParams.page || page, 10),
    perPage: parseInt(getQueryParams.perPage || perPage, 10),
  };

  const { loading, data, subscribeToMore, refetch } = useQuery(GET_TEMP_PRODUCT_LIST, {
    variables: {
      input: inputData,
    },
  });

  const [reSyncActions, { loading: reSyncLoading }] = useMutation(RE_SYNC_PRODUCT);

  const [bulkProductActions, { loading: loadingBulkProduct }] = useMutation(SYNC_SHOPIFY_PRODUCT, {
    refetchQueries: [
      {
        query: GET_TEMP_PRODUCT_LIST,
        variables: {
          input: inputData,
        },
      },
    ],
  });

  const tempData = baseHelper.getResponseData(data, constant.gql.GET_TEMP_PRODUCT_LIST);

  useEffect(() => {
    const paramValue = baseHelper.getQueryParams(location.search);
    const listFilter = (paramValue.list_filter && paramValue.list_filter.split(",")) || [];
    const listSearch = (paramValue.list_search && paramValue.list_search.split(",")) || [];
    const appliedFilters = [];
    listFilter.forEach((item, index) => {
      appliedFilters.push({
        key: item,
        value: listSearch[index],
      });
    });
    setAppliedFiltersValue(appliedFilters);
  }, [location.search, values]);

  useEffect(() => {
    subscribeToMore({
      document: TEMP_PRODUCT_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        return refetch();
      },
    });
  }, [subscribeToMore, refetch]);

  const handleSearchValue = (searchValue) => {
    const search = baseHelper.setQueryParams(location, { search: searchValue });
    setQueryValue(searchValue);
    navigate(`${location.pathname}?${search}`);
  };

  const handleQueryValueRemove = () => {
    const search = baseHelper.setQueryParams(location, { search: "" });
    setQueryValue("");
    navigate(`${location.pathname}?${search}`);
  };

  const handleSort = (select) => {
    const sort = select.split("_");
    if (sort && sort.length === 2) {
      const sortedValue = baseHelper.setQueryParams(location, { sort_name: sort[0], sort_order: sort[1] });
      setSortValue(select);
      navigate(`${location.pathname}?${sortedValue}`);
    }
  };

  const tabsData = [
    { id: "all", panelID: "all", content: cms("label.all"), value: constant.ALL },
    { id: "synced", panelID: "synced", content: cms("label.synced"), value: constant.SYNCED },
    { id: "unsynced", panelID: "unsynced", content: cms("label.notSynced"), value: constant.NOT_SYNCED },
  ];
  const handleTabChange = useCallback(
    (selectedTabIndex) => {
      setSelected(selectedTabIndex);
      setSelectedItems([]);
      const filterValue = baseHelper.setQueryParams(location, {
        filter: tabsData[selectedTabIndex].value,
        tab: selectedTabIndex,
      });
      navigate(`${location.pathname}?${filterValue}`);
    },
    [location, tabsData]
  );

  const reSyncAll = async () => {
    const formData = {
      isResync: true,
    };
    await reSyncActions({ variables: { input: { ...formData } } }).then((res) => {
      if (res) {
        const responseData = res.data;
        const responseDataError = res.errors;
        if (responseData) {
          const title = cms("message.success.requestBackground");
          setBanner({ isOpen: true, title, status: constant.SUCCESS });
        }
        if (responseDataError) {
          setBanner({ isOpen: true, title: responseDataError, status: constant.CRITICAL });
        }
      }
    });
  };

  const handleSync = async (eventKey, id = false) => {
    let formData = {};

    formData = {
      key: eventKey,
      ids: selectedItems,
      searchParams: {
        search: searchVal,
      },
    };

    if (id) {
      formData.ids = [id];
      setListLoading(`${eventKey}_${id}`);
    }

    await bulkProductActions({ variables: { input: { ...formData } } })
      .then((res) => {
        setListLoading(false);
        if (res) {
          const responseData = baseHelper.getResponseData(res.data, constant.gql.SYNC_SHOPIFY_PRODUCT);
          const responseDataError = baseHelper.getResponseError(res.data, constant.gql.SYNC_SHOPIFY_PRODUCT);
          if (responseData) {
            const title = cms("message.success.resyncProductBackground");
            setBanner({ isOpen: true, title, status: constant.SUCCESS });
            setSelectedItems([]);
          }
          if (responseDataError) {
            setBanner({ isOpen: true, title: responseDataError, status: constant.CRITICAL });
          }
        }
      })
      .catch((exception) => {
        setListLoading(false);
        setBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
      });
  };

  const bulkActions = [
    {
      content: cms("label.syncProducts"),
      disabled: selected === 1,
      onAction: () => handleSync(constant.APPROVE),
    },
  ];

  const handleChange = (val, _id) => {
    setSelectedButtonIndex(_id);
    if (val === constant.SYNC) {
      handleSync(constant.APPROVE, _id);
    }
  };

  const handleSelectedFilterRemove = () => {
    const search = baseHelper.setQueryParams(location, { list_search: "", list_filter: "" });
    setSelectedFilter("");
    setTaggedWith("");
    navigate(`${location.pathname}?${search}`);
  };

  const handleTaggedWithRemove = () => {
    const search = baseHelper.setQueryParams(location, { list_search: "", list_filter: "" });
    setTaggedWith("");

    navigate(`${location.pathname}?${search}`);
  };

  const disambiguateLabel = (key, value) => {
    switch (key) {
      case TAGGED_WITH:
        return `${value}`;
      default:
        return value;
    }
  };

  const isEmpty = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === "" || value == null;
  };

  const appliedFilters = [];
  if (!isEmpty(taggedWith)) {
    appliedFilters.push({
      key: TAGGED_WITH,
      label: disambiguateLabel(TAGGED_WITH, taggedWith),
      onRemove: handleTaggedWithRemove,
    });
  }

  if (!isEmpty(selectedFilter)) {
    appliedFilters.push({
      key: SELECTED_FILTER,
      label: disambiguateLabel(SELECTED_FILTER, selectedFilter),
      onRemove: handleSelectedFilterRemove,
    });
  }

  const handleClearAll = () => {
    handleTaggedWithRemove();
    handleSelectedFilterRemove();
  };

  const filters = [
    {
      key: SELECTED_FILTER,
      label: cms("label.filterBy"),
      filter: (
        <Filter
          dataToFetch={dataToFetch}
          setDataToFetch={setDataToFetch}
          taggedWith={taggedWith}
          setTaggedWith={setTaggedWith}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          isLiveConnect
        />
      ),
      shortcut: false,
    },
  ];

  if (selectedFilter) {
    filters.push({
      key: TAGGED_WITH,
      label: cms("label.filter"),
      filter: (
        <MoreFilters
          dataToFetch={dataToFetch}
          setDataToFetch={setDataToFetch}
          taggedWith={taggedWith}
          setTaggedWith={setTaggedWith}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />
      ),
      shortcut: false,
    });
  }

  return (
    <>
      {banner.isOpen && (
        <>
          <Banner
            isOpen={banner.isOpen}
            status={banner.status}
            title={banner.title}
            action={banner.action}
            onDismiss={() => setBanner({ isOpen: false })}
          />
          <br />
        </>
      )}
      <Banner title={cms("banner.addProductInstruction")} isOpen status={constant.INFO} />
      <br />
      <Banner title={cms("banner.importedProductInstruction")} isOpen />
      <br />
      <QuickReport />
      <Button variant="primary" onClick={reSyncAll}>
        {cms("label.resyncAllProduct")}
      </Button>
      <br />
      <br />

      <Card>
        <Tabs tabs={tabsData} selected={selected} onSelect={handleTabChange} />
        <ResourceList
          resourceName={{
            singular: baseHelper.ucFirst(constant.PRODUCT),
            plural: baseHelper.ucFirst(constant.PRODUCTS),
          }}
          items={tempData.tempProductList}
          idForItem={(item) => {
            const { _id } = item;
            return _id;
          }}
          selectable
          bulkActions={bulkActions}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          loading={loading}
          sortValue={sortValue}
          sortOptions={[
            { label: cms("label.dateDesc"), value: UPDATE_DESC },
            { label: cms("label.dateAsc"), value: UPDATE_ASC },
            { label: cms("label.productAsc"), value: TITLE_ASC },
            { label: cms("label.productDesc"), value: TITLE_DESC },
          ]}
          filters={filters}
          appliedFilters={appliedFilters}
          handleClearAll={handleClearAll}
          onSortChange={handleSort}
          onQueryChange={handleSearchValue}
          onQueryClear={handleQueryValueRemove}
          queryValue={queryValue}
          count={tempData.count}
          page={page}
          perPage={perPage}
          setPage={setPage}
          setPerPage={setPerPage}
          renderItem={(item) => {
            const { _id, updatedAt, title, images = [], vendor, published } = item;
            const thumbnailImage =
              images && images.length > 0
                ? imageHelper.resize({ url: images[0].url, type: constant.imageTypes.THUMBNAIL })
                : constant.NOIMAGESNAP;

            const syncedStatusType = (published && constant.SYNCED) || constant.NOT_SYNCED;
            const syncedStatus = (published && cms("label.synced")) || cms("label.notSynced");
            return (
              <ResourceItem id={_id}>
                <InlineStack align="space-between">
                  <InlineStack gap={"400"}>
                    <Thumbnail size={constant.LARGE} source={thumbnailImage} alt={`${title} image`} />
                    <BlockStack gap={"100"}>
                      <Text as="h2" variant="headingMd">
                        <Text as="span" fontWeight="semibold">
                          {title}
                        </Text>
                      </Text>
                      <Text variant="bodySm" as="p">
                        {`${cms("common.label.by")} `}
                        <b>{vendor}</b>
                        {` ${cms("common.label.on")} ${baseHelper.formatDate(updatedAt)}`}
                      </Text>
                      <InlineStack>
                        <Badge tone={baseHelper.getBadgeType(syncedStatusType)}>
                          {baseHelper.ucFirst(syncedStatus)}
                        </Badge>
                      </InlineStack>
                    </BlockStack>
                  </InlineStack>

                  <Popover
                    // eslint-disable-next-line no-underscore-dangle
                    active={activePopover[_id]}
                    // eslint-disable-next-line no-underscore-dangle
                    setActive={() => setActivePopover({ [_id]: !activePopover[_id] })}
                    loading={_id === selectedButtonIndex && listLoading}
                    options={[
                      {
                        content: cms("label.publish"),
                        onAction: () => handleChange(constant.SYNC, _id),
                        disabled: published,
                      },
                    ]}
                  />
                </InlineStack>
              </ResourceItem>
            );
          }}
        />
      </Card>
    </>
  );
};
export default withFeature(withErrorBoundary(List), {
  feature: constant.SHOPIFY_PRODUCT,
});
